import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ExternalLink, Check, X, UserPlus, Play, PauseCircle, PackageCheck, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLOR = {
  Draft: 'bg-slate-100 text-slate-700 border-slate-200',
  Released: 'bg-blue-100 text-blue-800 border-blue-200',
  InProgress: 'bg-amber-100 text-amber-800 border-amber-200',
  Paused: 'bg-rose-100 text-rose-800 border-rose-200',
  Completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Closed: 'bg-slate-900 text-white border-slate-900',
};

/**
 * ShopWoSideDrawer — opens a right-side sheet with the shop-floor WO's live
 * details so a supervisor can approve, change status, or reassign without
 * leaving the parent WOv2 detail page.
 */
const ShopWoSideDrawer = ({ open, onOpenChange, api, shopWoSummary, onChanged }) => {
  const navigate = useNavigate();
  const [wo, setWo] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState('');

  const refresh = async () => {
    if (!shopWoSummary?.shop_work_order_id) return;
    setLoading(true);
    try {
      const [w, u] = await Promise.all([
        api().get(`/work-orders/${shopWoSummary.shop_work_order_id}`),
        api().get('/users').catch(() => ({ data: [] })),
      ]);
      setWo(w.data);
      setUsers(u.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) refresh(); /* eslint-disable-next-line */ }, [open, shopWoSummary?.shop_work_order_id]);

  const action = async (fn, label) => {
    try {
      await fn();
      toast.success(label);
      await refresh();
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || `${label} failed`); }
  };

  const approve = () => action(() => api().post(`/work-orders/${wo.id}/approve?notify_operator=true`), 'Approved');
  const reject = () => action(() => api().post(`/work-orders/${wo.id}/reject?reason=${encodeURIComponent('Rejected via WOv2 side-drawer')}`), 'Rejected');
  const changeStatus = (s) => action(() => api().put(`/work-orders/${wo.id}/status?new_status=${encodeURIComponent(s)}`), `Status → ${s}`);
  const reassign = async () => {
    if (!assigning) return;
    const next = Array.from(new Set([...(wo.assigned_users || []), assigning]));
    await action(() => api().put(`/work-orders-v2/shop-wos/${wo.id}/assign`, { assigned_users: next }), 'Reassigned');
    setAssigning('');
  };
  const removeAssignee = async (uid) => {
    const next = (wo.assigned_users || []).filter(u => u !== uid);
    await action(() => api().put(`/work-orders-v2/shop-wos/${wo.id}/assign`, { assigned_users: next }), 'Removed');
  };

  const progressPct = Math.min(100, Math.round(shopWoSummary?.progress_pct || 0));
  const notPending = wo && wo.approval_status && wo.approval_status !== 'pending';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="shop-wo-drawer">
        <SheetHeader className="space-y-2">
          <SheetTitle className="flex items-center gap-2 flex-wrap">
            <span className="mono text-base">{shopWoSummary?.shop_work_order_number}</span>
            <Badge className={`${STATUS_COLOR[wo?.status] || 'bg-slate-100 text-slate-700 border-slate-200'} border rounded-sm text-[11px]`}>{wo?.status || shopWoSummary?.status}</Badge>
            {wo?.approval_status && (
              <Badge variant="outline" className={`rounded-sm text-[11px] ${wo.approval_status === 'approved' ? 'border-emerald-300 text-emerald-700' : wo.approval_status === 'rejected' ? 'border-red-300 text-red-700' : 'border-amber-300 text-amber-700'}`}>
                {wo.approval_status}
              </Badge>
            )}
          </SheetTitle>
          <div className="text-xs text-slate-500">Shop-Floor Work Order · Actions apply immediately</div>
        </SheetHeader>

        {loading && !wo ? (
          <div className="py-10 text-sm text-slate-500">Loading…</div>
        ) : !wo ? (
          <div className="py-10 text-sm text-red-500">Shop WO could not be loaded.</div>
        ) : (
          <div className="mt-4 space-y-5">
            {/* Progress */}
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-500">Progress</span>
                <span className="mono">{shopWoSummary?.completed_quantity || 0}/{shopWoSummary?.target_quantity || 0} ({progressPct}%)</span>
              </div>
              <Progress value={progressPct} className="h-2" />
            </div>

            {/* Approval row */}
            <div className="flex items-center gap-2 flex-wrap">
              {!notPending && (
                <>
                  <Button size="sm" onClick={approve} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="shop-wo-approve"><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                  <Button size="sm" variant="outline" onClick={reject} className="rounded-sm text-red-700 border-red-200 hover:bg-red-50" data-testid="shop-wo-reject"><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                </>
              )}
              <Button size="sm" variant="outline" className="rounded-sm" onClick={refresh} data-testid="shop-wo-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
              <Button size="sm" variant="outline" className="rounded-sm ml-auto" onClick={() => navigate(`/work-order/${wo.id}`)} data-testid="shop-wo-jump"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Open full page</Button>
            </div>

            {/* Status transitions */}
            <div>
              <div className="text-xs text-slate-500 mb-2">Status transitions</div>
              <div className="flex items-center gap-1 flex-wrap">
                {['Released', 'InProgress', 'Paused', 'Completed', 'Closed'].map(s => (
                  <Button key={s} size="sm" variant="outline" disabled={wo.status === s} onClick={() => changeStatus(s)} className="rounded-sm text-xs" data-testid={`shop-wo-status-${s}`}>
                    {s === 'InProgress' && <Play className="w-3 h-3 mr-1" />}
                    {s === 'Paused' && <PauseCircle className="w-3 h-3 mr-1" />}
                    {s === 'Completed' && <PackageCheck className="w-3 h-3 mr-1" />}
                    {s}
                  </Button>
                ))}
              </div>
            </div>

            {/* Reassign */}
            <div>
              <div className="text-xs text-slate-500 mb-2">Assigned to</div>
              <div className="flex items-center flex-wrap gap-1 mb-2">
                {(wo.assigned_users || []).length === 0 && <div className="text-xs text-slate-400 italic">no-one assigned</div>}
                {(wo.assigned_users || []).map(uid => {
                  const u = users.find(x => x.id === uid);
                  return (
                    <Badge key={uid} variant="outline" className="rounded-sm text-[11px]">
                      {u?.full_name || u?.email || uid}
                      <button className="ml-1 text-slate-400 hover:text-red-500" onClick={() => removeAssignee(uid)} data-testid={`shop-wo-unassign-${uid}`}><X className="w-3 h-3" /></button>
                    </Badge>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <Select value={assigning} onValueChange={setAssigning}>
                  <SelectTrigger className="rounded-sm h-9 w-56" data-testid="shop-wo-assign-select"><SelectValue placeholder="Add teammate…" /></SelectTrigger>
                  <SelectContent>
                    {users.filter(u => !(wo.assigned_users || []).includes(u.id)).map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={reassign} disabled={!assigning} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="shop-wo-assign-btn"><UserPlus className="w-3.5 h-3.5 mr-1" /> Add</Button>
              </div>
            </div>

            {/* Lots consumed snapshot */}
            {(shopWoSummary?.lots_consumed || []).length > 0 && (
              <div>
                <div className="text-xs text-slate-500 mb-2">Lots consumed</div>
                <div className="flex flex-wrap gap-1">
                  {shopWoSummary.lots_consumed.slice(0, 20).map(l => (
                    <Badge key={l.lot} variant="outline" className="rounded-sm text-[10px] mono" title={(l.component_skus || []).join(', ')}>
                      {l.lot} <span className="text-slate-500 ml-1">({l.qty})</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {wo.notes && <div className="text-xs text-slate-500 whitespace-pre-line border-t border-slate-100 pt-3">{wo.notes}</div>}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default ShopWoSideDrawer;
